import React, { useEffect, useRef, useState } from 'react';
import FormError from '../../../FormError';
import Spinner from '../shared/Spinner';
import { apiFetch } from '../shared/api';
import { formatPhoneAsTyped } from './formatPhoneAsTyped';

function deriveSlugFromName(name) {
    const firstWord = (name || '').trim().split(/\s+/)[0] || '';
    return firstWord.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

function fieldError(errors, field) {
    return errors && errors[field] && errors[field][0];
}

export default function ManualBusinessFields({
    values,
    onChange,
    errors,
    industries,
    brandedLinkBase,
    slugLimit,
    slugCheckUrl,
}) {
    const slugEditedRef = useRef(Boolean(values.slug));
    const [slugCheck, setSlugCheck] = useState('idle');
    const [slugSuggestion, setSlugSuggestion] = useState(null);
    const mountedRef = useRef(true);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    const handleNameChange = (nextName) => {
        onChange('name', nextName);
        if (!slugEditedRef.current) {
            onChange('slug', deriveSlugFromName(nextName));
        }
    };

    const handleSlugChange = (nextSlug) => {
        slugEditedRef.current = true;
        setSlugCheck('idle');
        setSlugSuggestion(null);
        onChange('slug', nextSlug);
    };

    const handleSlugBlur = () => {
        if (!values.slug || values.slug.length > slugLimit) {
            return;
        }

        setSlugCheck('checking');
        apiFetch(slugCheckUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ slug: values.slug }),
        }).then(({ status, json }) => {
            if (!mountedRef.current) {
                return;
            }
            if (status >= 200 && status < 300) {
                const available = json?.available === true;
                setSlugCheck(available ? 'idle' : 'taken');
                setSlugSuggestion(available ? null : (json?.suggestion || null));
            } else {
                setSlugCheck('unverified');
            }
        }).catch((error) => {
            console.error(error);
            if (!mountedRef.current) {
                return;
            }
            setSlugCheck('unverified');
        });
    };

    const remaining = slugLimit - (values.slug || '').length;
    const badgeClass = remaining < 0 ? 'badge-danger' : (remaining <= 5 ? 'badge-warning' : 'badge-light');

    return (
        <div className="demo-add-manual-fields" data-testid="demo-add-manual">
            <FormError error_message={fieldError(errors, 'google_place_id')} />

            <div className="row">
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="demo-add-slug">{__('Branded Link')}</label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">{brandedLinkBase}</span>
                            </div>
                            <input
                                id="demo-add-slug"
                                type="text"
                                className="form-control"
                                value={values.slug}
                                onChange={(event) => handleSlugChange(event.target.value)}
                                onBlur={handleSlugBlur}
                            />
                            {slugCheck === 'checking' &&
                                <div className="input-group-append">
                                    <span className="input-group-text">
                                        <Spinner label={__('Checking branded link')} />
                                    </span>
                                </div>
                            }
                        </div>
                        <div className="demo-island-slug-meta">
                            <small className="form-text text-muted">
                                {__('Branded Link is :base:placeholder', { ':base': brandedLinkBase, ':placeholder': 'XYZ' })}
                            </small>
                            <span className={`badge demo-island-slug-badge demo-island-slug-remaining ${badgeClass}`} data-testid="demo-add-slug-badge">
                                {__('Characters Remaining: :n', { ':n': remaining })}
                            </span>
                        </div>
                        {remaining < 0 &&
                            <div className="text-danger small">{__('Branded link is over the limit')}</div>
                        }
                        {slugCheck === 'taken' &&
                            <FormError
                                error_message={slugSuggestion
                                    ? __('This branded link is already taken. Try :suggestion.', { ':suggestion': slugSuggestion })
                                    : __('This branded link is already taken.')}
                            />
                        }
                        {slugCheck === 'unverified' &&
                            <small className="form-text text-warning">
                                {__('The branded link could not be verified right now; it will be checked when you create the demo account.')}
                            </small>
                        }
                        <FormError error_message={fieldError(errors, 'slug')} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="demo-add-address">{__('Address')}</label>
                        <input
                            id="demo-add-address"
                            type="text"
                            className="form-control"
                            value={values.address}
                            onChange={(event) => onChange('address', event.target.value)}
                        />
                        <FormError error_message={fieldError(errors, 'address')} />
                    </div>

                    {/* Sits with Address rather than in the right column: it
                        controls whether that address is shown. */}
                    <div className="custom-control custom-checkbox mb-3">
                        <input
                            id="demo-add-show-address"
                            type="checkbox"
                            className="custom-control-input"
                            checked={Boolean(values.show_address)}
                            onChange={(event) => onChange('show_address', event.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor="demo-add-show-address">
                            {__('Show address on Feedback page')}
                        </label>
                    </div>

                    <div className="form-group">
                        <label htmlFor="demo-add-phone">{__('Phone')}</label>
                        <input
                            id="demo-add-phone"
                            type="tel"
                            className="form-control"
                            value={values.phone}
                            onChange={(event) => onChange('phone', formatPhoneAsTyped(event.target.value, values.phone))}
                        />
                        <FormError error_message={fieldError(errors, 'phone')} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="demo-add-display-url-as">{__('Display URL as')}</label>
                        <input
                            id="demo-add-display-url-as"
                            type="text"
                            className="form-control"
                            value={values.display_url_as}
                            onChange={(event) => onChange('display_url_as', event.target.value)}
                        />
                        <FormError error_message={fieldError(errors, 'display_url_as')} />
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="demo-add-name">{__('Name')}</label>
                        <input
                            id="demo-add-name"
                            type="text"
                            className="form-control"
                            value={values.name}
                            onChange={(event) => handleNameChange(event.target.value)}
                        />
                        <FormError error_message={fieldError(errors, 'name')} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="demo-add-url">{__('URL')}</label>
                        <input
                            id="demo-add-url"
                            type="text"
                            className="form-control"
                            value={values.url}
                            onChange={(event) => onChange('url', event.target.value)}
                        />
                        <FormError error_message={fieldError(errors, 'url')} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="demo-add-industry">{__('Industry')}</label>
                        <select
                            id="demo-add-industry"
                            className="form-control"
                            value={values.industry_id}
                            onChange={(event) => onChange('industry_id', event.target.value)}
                        >
                            <option value="">{__('Select an industry')}</option>
                            {industries.map((industry) => (
                                <option key={industry.id} value={industry.id}>{industry.name}</option>
                            ))}
                        </select>
                        <FormError error_message={fieldError(errors, 'industry_id')} />
                    </div>
                </div>
            </div>
        </div>
    );
}
